/**
 * The virtual coin.
 *
 * A real toss happens with a real coin, and this does not replace it — the manual selector
 * stays, and is what a scorer standing at the middle uses. What this is for is the game
 * where nobody has a coin, which at a maidan is most of them, and where the alternative is
 * one captain calling it on his phone's calculator app.
 *
 * The randomness is a parameter rather than a call to `Math.random()`, for the ordinary
 * reason: a coin whose result cannot be pinned in a test is a coin nobody can prove is fair,
 * and a screen that flips one is otherwise only checkable by watching it.
 */
import type { TeamId } from "@sportsos/engine";

export type CoinFace = "heads" | "tails";

/** Heads is the first team, tails the second. Stated once so the animation and the result
 *  cannot disagree about which side of the coin belongs to whom. */
export function faceFor(random: number): CoinFace {
  return random < 0.5 ? "heads" : "tails";
}

/** Which side a face hands the toss to. */
export function winnerOf(face: CoinFace, teams: readonly [TeamId, TeamId]): TeamId {
  return face === "heads" ? teams[0] : teams[1];
}

/**
 * Up to two letters for the coin's faces, from a team name.
 *
 * Initials of the first two words — "Royal Challengers" is RC — falling back to the first two
 * letters of a one-word name, so "Tigers" is TI rather than a lone T that reads as a typo.
 * A side with no name yet gets `?`, which is what the setup screen shows anyway until one is
 * typed.
 */
export function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter((word) => word !== "");
  if (words.length === 0) return "?";
  if (words.length === 1) return (words[0] ?? "").slice(0, 2).toUpperCase();
  return words
    .slice(0, 2)
    .map((word) => (word[0] ?? "").toUpperCase())
    .join("");
}
